import { Injectable } from '@angular/core';
import { Subscription, BehaviorSubject, Observable, combineLatest, forkJoin, from, of, throwError } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { PortalService } from './portal.service';
import { convertTimestampsPipe } from '../pipes/convert-firestore-timestamp.pipe';
import { AptitudeScore, AptitudeTask, Assessment, Counselor, CounselorHistory, Evaluee, EvalueeDataUpdateForm, EvalueeDataUpdateTransfer, EvalueeStatusOptions, InterestScore, Portal, Register, User } from '@career-scope/models';
import { AuthService } from './auth.service';
import { collection, collectionData, CollectionReference, doc, docData, DocumentReference, Firestore, getDoc, increment, query, setDoc, updateDoc, where, writeBatch, serverTimestamp, getDocs, deleteDoc } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { NotificationService } from './notification.service';
import { createAssessmentObject } from './evaluee-registration.service';

@Injectable({
  providedIn: 'root'
})
export class EvalueeService {
  evaluee = new BehaviorSubject<Evaluee | null | undefined>(null);
  evaluee$: Observable<Evaluee | undefined> = of(undefined);
  evalueeSubscription: Subscription | null = null;

  assessment = new BehaviorSubject<Assessment | null | undefined>(null);
  assessment$: Observable<Assessment | undefined> = of(undefined);
  assessmentSubscription: Subscription | null = null;

  constructor(
    private firestore: Firestore,
    private as: AuthService,
    private ps: PortalService,
    private functions: Functions,
    private notif: NotificationService
  ) {
    this.as.user$.pipe(
      tap(user => {
        if (user) {
          return;
        }

        this.evaluee$ = of(undefined);
        this.assessment$ = of(undefined);
        this.evalueeSubscription?.unsubscribe();
        this.assessmentSubscription?.unsubscribe();
        this.evaluee.next(null);
        this.assessment.next(null);
      })
    ).subscribe();
  }

  setEvalueeObservable(portalId: string, uid: string) {
    const evaluee = this.evaluee.value;
    if (evaluee?.portalId === portalId && evaluee?.uid === uid) {
      return;
    }

    this.evaluee.next(null);
    this.evalueeSubscription?.unsubscribe();

    const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${uid}`) as DocumentReference<Evaluee>;
    this.evaluee$ = docData(evalueeDocRef).pipe(
      convertTimestampsPipe(),
      tap((evaluee) => {
        if (!evaluee) {
          this.evaluee.next(undefined);
          return;
        }

        // TODO: Look at updating this when we save the grade to Firestore
        const adjustedGrade = this.calculateGrade(evaluee);
        this.evaluee.next({ ...evaluee, adjustedGrade });
        this.setAssessmentObservable(portalId, uid, evaluee.currentAssessmentId);
      })
    );

    this.evalueeSubscription = this.evaluee$.subscribe();
  }

  setAssessmentObservable(portalId: string, evalueeId: string, assessmentId: string) {
    const assessment = this.assessment.value;
    if (assessment?.portalId === portalId && assessment?.evalueeId === evalueeId && assessment?.id === assessmentId) {
      return;
    }

    this.assessment.next(null);
    this.assessmentSubscription?.unsubscribe();

    const assessmentDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${evalueeId}/assessments/${assessmentId}`) as DocumentReference<Assessment>;
    this.assessment$ = docData(assessmentDocRef).pipe(
      // Assuming convertTimestampsPipe is a custom operator to convert timestamps
      convertTimestampsPipe()
    );

    this.assessmentSubscription = this.assessment$.subscribe(res => {
      if (res) {
        this.assessment.next(res);
      }
    });
  }

  // This creates a subset of the interest scores that are ranked in the top 3 (including ties)
  // The top interests are used to filter occupations to align with high interest
  topInterestScores(interestScores: InterestScore[]): InterestScore[] {
    const scores = interestScores?.filter(interest => interest.rank > 0);
    scores?.sort((a, b) => a.rank - b.rank);
    return scores;
  }

  closeOut() {
    this.evaluee.next(null);
    this.evalueeSubscription?.unsubscribe();
  }

  updateEvaluee(evalueeUpdates: Partial<Evaluee>) {
    const evaluee = this.evaluee.value;
    if (evaluee) {
      const portalId = this.getPortalId(evaluee);
      const uid = this.getUid(evaluee);
      const updatedEvaluee = { ...evaluee, ...evalueeUpdates };
      updatedEvaluee.lastModifiedDate = new Date(Date.now());

      const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${uid}`) as DocumentReference<Evaluee>;
      setDoc(evalueeDocRef, <Partial<Evaluee>>updatedEvaluee, { merge: true });
    }
  }

  updateAssessment(assessmentUpdates: Partial<Assessment>) {
    const assessment = this.assessment.value;
    if (!assessment) {
      return;
    }
    const updatedAssessment = { ...assessment, ...assessmentUpdates };

    const assessmentDocRef = doc(this.firestore, `portals/${assessment.portalId}/evaluees/${assessment.evalueeId}/assessments/${assessment.id}`) as DocumentReference<Assessment>;
    setDoc(assessmentDocRef, <Partial<Assessment>>updatedAssessment, { merge: true });
  }

  resetExercise(assessment: Assessment, task: AptitudeTask) {
    // Evaluee properties
    const exerciseStatus = 'Exercise ' + task.name + ' reset';
    const lastActivity = 'Exercise ' + task.name + ' reset';
    const lastActivityDate = new Date(Date.now());
    const lastModifiedDate = new Date(Date.now());
    const status = 'in progress';

    // Assessment properties
    // Remove all evalueeAnswers for the task ID
    const updatedAnswers = assessment.evalueeAnswers.filter(answer => answer.taskId !== task.id);

    // Remove item from taskResults associated with task ID
    const taskResults = assessment.taskResults.filter(result => result.taskId !== task.id);
    const exercisesComplete = null;
    const aptitudeScores: AptitudeScore[] = [];

    this.updateEvaluee({ exerciseStatus, status, lastActivity, lastActivityDate, lastModifiedDate });
    this.updateAssessment({ aptitudeScores, exercisesComplete, taskResults, evalueeAnswers: updatedAnswers });
  }

  deleteEvaluee(evaluee: Evaluee) {
    this.deleteEvalueeFromFirestore(String(evaluee.uid || evaluee.sqlId), evaluee.userId || '', evaluee?.registrationId || null, evaluee.portalId, evaluee.status);
  }

  deleteEvalueeFromFirestore(uid: string | undefined, userId: string, registrationId: string | null | undefined, portalId: string, status: EvalueeStatusOptions) {
    if (!uid) {
      console.error('UID is undefined');
      return;
    }

    const batch = writeBatch(this.firestore);

    // Remove evaluee from portals/portal/Evaluee
    const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${uid}`);
    batch.delete(evalueeDocRef);

    // All users should have a registrationId. 
    // If they do not then they were imported and never registered or created via self registration
    if (registrationId) {
      const registrationDocRef = doc(this.firestore, `registrations/${registrationId}`);
      batch.delete(registrationDocRef);
    }

    // Remove user from users
    if (userId) {
      const userDocRef = doc(this.firestore, `users/${userId}`);
      batch.delete(userDocRef);
      // Cloud function deletes corresponding auth record for user
    }

    // Commit the batch
    batch.commit()
      .then(() => {

        if (status === 'in progress' || status === 'new') {
          this.ps.returnAdministration();
        }
        this.notif.openDismissibleSnackbar('Assessment Cancelled!');
      })
      .catch(error => {
        console.error('Error deleting evaluee: ', uid, ' from portal: ', portalId, error);
      });
  }


  updateEvalueeCounselor(counselor: Counselor, evaluee: Evaluee) {
    const currentCounselor: Counselor = evaluee.counselor;
    const counselorHistory: CounselorHistory[] = evaluee.counselorHistory || [];
    this.updateEvaluee(
      {
        counselor,
        counselorHistory: [{ counselor: currentCounselor, endDate: new Date(Date.now()) }, ...counselorHistory],
      }
    );
  }

  archiveEvaluee() {
    this.updateEvaluee({ status: 'archived' });
  }

  unarchiveEvaluee() {
    this.updateEvaluee({ status: 'completed' });
  }

  viewMyPathAccess(evaluee: Evaluee, access: boolean) {
    this.updateEvaluee({ assessmentSettings: { ...evaluee.assessmentSettings, viewMyPath: access } });
  }

  sendDataExternally(portalId: string, evalueeId: string, assessmentId?: string) {
    const sendDataExternalSeekerOnDemandCallable = httpsCallable<
      { portalId: string; evalueeId: string; assessmentId?: string },
      { success: boolean }
    >(this.functions, 'sendDataExternalSeekerOnDemand', {
      timeout: 290_000,
    });
    return from(sendDataExternalSeekerOnDemandCallable({ portalId, evalueeId, assessmentId })).pipe(
      take(1),
      map(result => result.data)
    );
  }

  private getPortalId(evaluee: Evaluee): string {
    return evaluee.portalId;
  }

  private getUid(evaluee: Evaluee): string {
    return evaluee.uid || String(evaluee.sqlId);
  }


  private calculateGrade(evaluee: Evaluee): number | null {

    let grade = evaluee.currentGrade ? Number(String(evaluee.currentGrade).replace(/[^0-9]/g, '')) : null; // Currently stored as a string

    // If no grade but younger than 17, set a grade level.
    if (!grade) {
      if (evaluee.age) {
        grade = evaluee.age <= 14 ? 8 : evaluee.age === 15 ? 9 : evaluee.age === 16 ? 10 : null;
      }
    }
    if (grade) {
      grade = grade < 8 ? 8 : grade; // Anything lower than 8th grade is considered 8th gread
      grade = grade > 10 ? null : grade; // Anything higher than 10th grade is considered an adult
    }
    return grade;
  }

  // Logic for this is also in apps/career-scope auth.service
  checkForExistingEmail(email: string): Observable<boolean> {
    const registrationsCollectionRef = collection(this.firestore, 'registrations') as CollectionReference<Register>;
    const usersCollectionRef = collection(this.firestore, 'users') as CollectionReference<User>;

    const registrationsQuery = query(registrationsCollectionRef, where('email', '==', email));
    const usersQuery = query(usersCollectionRef, where('email', '==', email));

    const registrations$ = collectionData<Register>(registrationsQuery);
    const users$ = collectionData<User>(usersQuery);

    return combineLatest([registrations$, users$]).pipe(
      map(([registrations, users]) => {
        return !!registrations.length || !!users.length;
      })
    );
  }

  checkForExistingUsername(username: string): Observable<boolean> {
    const usersCollectionRef = collection(this.firestore, 'users') as CollectionReference<User>;
    const usersQuery = query(usersCollectionRef, where('username', '==', username.toLowerCase()));

    const users$ = collectionData<User>(usersQuery);

    return users$.pipe(
      map(users => {
        return !!users.length;
      })
    );
  }

  calculateResults() {
    this.updateAssessment({ aptitudeScores: [], interestResults: [], topInterestScores: [], interestScores: [] });
  }

  updateEvalueeData(evalueeDataUpdate: EvalueeDataUpdateForm) {

    const dataToUpdate: EvalueeDataUpdateForm = {};

    return this.evaluee.pipe(
      take(1),
      switchMap(evaluee => {
        if (evaluee) {

          if (evalueeDataUpdate.fullName != evaluee.fullName && evalueeDataUpdate.fullName) {
            dataToUpdate.fullName = evalueeDataUpdate.fullName;
            dataToUpdate.lowerCaseFullName = evalueeDataUpdate.fullName?.toLowerCase();
          }

          if (evalueeDataUpdate.logonName != evaluee.logonName) {
            dataToUpdate.logonName = evalueeDataUpdate.logonName;
          }

          if (evalueeDataUpdate.secret) {
            dataToUpdate.secret = evalueeDataUpdate.secret;
          }

          if (evalueeDataUpdate.age != evaluee.age) {
            dataToUpdate.age = evalueeDataUpdate.age;
          }

          if (evalueeDataUpdate.counselor && evalueeDataUpdate.counselor?.id != evaluee.counselor.id) {
            dataToUpdate.counselor = evalueeDataUpdate.counselor;
            const counselorHistory = evaluee.counselorHistory || [];
            counselorHistory.push({
              counselor: evaluee.counselor,
              endDate: new Date(Date.now())
            });
            dataToUpdate.counselorHistory = counselorHistory;
          }

          const newData = JSON.stringify(evalueeDataUpdate.identifiers?.map(o => ({ id: o.id, identifier: o.identifier })).sort((a, b) => a.id - b.id));
          const current = JSON.stringify(evaluee.identifiers?.map(o => ({ id: o.id, identifier: o.identifier })).sort((a, b) => a.id - b.id));

          if (newData !== current) {
            dataToUpdate.identifiers = evalueeDataUpdate.identifiers;
            dataToUpdate.identifierCategories = evalueeDataUpdate.identifiers?.map(identifier => identifier.id) || [];
          }

          if (Object.keys(dataToUpdate).length === 0) {
            throw 'No changes made';
          }

          if (dataToUpdate.logonName || dataToUpdate.secret) {
            const portalId = this.getPortalId(evaluee);
            const uid = this.getUid(evaluee);

            const evalueeDataUpdateTransfer: EvalueeDataUpdateTransfer = {
              portalId,
              uid,
              data: dataToUpdate
            };

            if (dataToUpdate.logonName) {
              if (!evaluee.registeredWithUsername) {
                return this.checkForExistingEmail(dataToUpdate.logonName).pipe(
                  take(1),
                  switchMap((exists) => {
                    if (exists) {
                      throw new Error('Email already exists');
                    }

                    return this.updateEvalueeDataTransfer(evalueeDataUpdateTransfer).pipe(take(1));
                  })
                );
              }

              return this.checkForExistingUsername(dataToUpdate.logonName).pipe(
                take(1),
                switchMap((exists) => {
                  if (exists) {
                    throw new Error('Username already exists');
                  }

                  return this.updateEvalueeDataTransfer(evalueeDataUpdateTransfer).pipe(take(1));
                })
              );
            }


            return this.updateEvalueeDataTransfer(evalueeDataUpdateTransfer).pipe(take(1));
          }

          return this.updateEvalueeAsObservable(dataToUpdate);
        }

        throw 'Evaluee not found';
      })
    );
  }

  updateEvalueeDataTransfer(evalueeDataUpdateTransfer: EvalueeDataUpdateTransfer): Observable<string> {
    const updateEvalueeDataCallable = httpsCallable<EvalueeDataUpdateTransfer, string>(this.functions, 'updateEvalueeData');
    return from(updateEvalueeDataCallable(evalueeDataUpdateTransfer)).pipe(
      take(1),
      map(result => result.data)
    );
  }

  updateEvalueeAsObservable(evalueeUpdates: Partial<Evaluee>): Observable<string> {
    return this.evaluee.pipe(
      take(1),
      switchMap(evaluee => {
        if (evaluee) {
          const portalId = this.getPortalId(evaluee);
          const uid = this.getUid(evaluee);
          evalueeUpdates = { ...evalueeUpdates, lastModifiedDate: new Date() };

          const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${uid}`);
          return from(updateDoc(evalueeDocRef, evalueeUpdates)).pipe(
            map(() => uid)
          );
        } else {
          throw new Error('Evaluee not found');
        }
      })
    );
  }

  batchArchiveEvalueeByPortalEvalueeIds(portalId: string, evalueeIds: string[]): Observable<void> {
    if (evalueeIds.length >= 500) {
      return throwError(() => new Error('Batch size cannot exceed 500.'));
    }

    const evalueeUpdates: Partial<Evaluee> = { status: 'archived' };
    
    const batch = writeBatch(this.firestore);

    for (const evalueeId of evalueeIds) {
      const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${evalueeId}`);
      batch.update(evalueeDocRef, evalueeUpdates);
    }

    return from(batch.commit());
  }

  batchRevokeAssessmentsByEvaluees(evaluees: Evaluee[]): Observable<void> {
    if (evaluees.length >= 100) {
      return throwError(() => new Error('Batch size cannot exceed 100 for this action.'));
    }

    const portalId = evaluees[0].portalId;

    const statusesToReturnAdmins = ['new', 'in progress'] as EvalueeStatusOptions[];

    const adminCountToReturn = evaluees.filter(evaluee => statusesToReturnAdmins.includes(evaluee.status)).length;

    const batch = writeBatch(this.firestore);

    for (const evaluee of evaluees) {
      // Assessments sub collection of evaluee will be deleted by doc listener cloud function
      const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${evaluee.uid}`);
      batch.delete(evalueeDocRef);

      if (evaluee.registrationId) {
        const registrationDocRef = doc(this.firestore, `registrations/${evaluee.registrationId}`);
        batch.delete(registrationDocRef);
      }

      if (evaluee.userId) {
        const userDocRef = doc(this.firestore, `users/${evaluee.userId}`);
        batch.delete(userDocRef);
      }
    }

    const portalDocRef = doc(this.firestore, `portals/${portalId}`);
    batch.update(portalDocRef, <Partial<Portal>>{ availableAdmins: increment(adminCountToReturn) as unknown as number, lastModifiedDate: new Date(Date.now()) });

    return from(batch.commit());
  }

  async startNewAssessment(evaluee: Evaluee) {
    if (!evaluee || !evaluee.uid) {
      this.notif.openDismissibleSnackbar('Error: Evaluee data is missing.');
      console.error('Cannot start new assessment, evaluee data missing', evaluee);
      return;
    }

    // Check if portal has available assessments
    const portal = this.ps.portal.value;
    if (!portal || portal.availableAdmins <= 0) {
      this.notif.openDismissibleSnackbar('Error: No available assessments.');
      return;
    }

    const portalId = this.getPortalId(evaluee);
    const evalueeId = this.getUid(evaluee);
    const previousAssessmentId = evaluee.currentAssessmentId;

    const newAssessmentRef = doc(collection(this.firestore, `portals/${portalId}/evaluees/${evalueeId}/assessments`));
    const newAssessmentId = newAssessmentRef.id;

    // Use shared function for assessment creation
    const newAssessmentData: Assessment = createAssessmentObject(evalueeId, portalId, newAssessmentId, evaluee.counselor?.id);
    newAssessmentData.status = 'in progress';
    newAssessmentData.age = evaluee.age || null;
    newAssessmentData.gender = evaluee.gender || null;
    newAssessmentData.currentGrade = evaluee.currentGrade || null;
    newAssessmentData.lastModifiedDate = new Date(Date.now());

    const updatedAssessmentIds = [...((evaluee.assessmentIds || [])), previousAssessmentId];

    const evalueeUpdates: Partial<Evaluee> = {
      currentAssessmentId: newAssessmentId,
      assessmentIds: updatedAssessmentIds,
      // For re-evaluations, temporarily set status to 'in progress' so it shows in Assess tab
      // The status will be updated back to 'completed' when the new assessment is completed
      status: 'in progress',
      interestComplete: null,
      exercisesComplete: null,
      completedDate: null,
      interestStatus: '',
      exerciseStatus: '',
      lastActivity: 'New assessment started',
      lastActivityDate: new Date(Date.now()),
      lastModifiedDate: new Date(Date.now()),
    };

    // Perform batch write
    const batch = writeBatch(this.firestore);
    const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${evalueeId}`);
    const portalDocRef = doc(this.firestore, `portals/${portalId}`);

    batch.set(newAssessmentRef, newAssessmentData);
    batch.update(evalueeDocRef, evalueeUpdates);
    batch.update(portalDocRef, <Partial<Portal>>{ 
      availableAdmins: increment(-1) as unknown as number,
      lastModifiedDate: new Date(Date.now())
    });

    try {
      await batch.commit();
      
      // Manually trigger portal statistics update for re-evaluations
      try {
        const forcePortalStatisticsUpdateCallable = httpsCallable(this.functions, 'forcePortalStatisticsUpdate');
        await forcePortalStatisticsUpdateCallable({ 
          portalId
        });
        
        // Force refresh portal data to get updated counts
        this.ps.loadPortal(portalId, evaluee.counselor?.id || '');
        
      } catch (error) {
        console.error('Error updating portal statistics:', error);
        // Fallback: refresh portal data
        this.ps.loadPortal(portalId, evaluee.counselor?.id || '');
      }
      
      this.notif.openDismissibleSnackbar('New assessment started successfully.');
      this.setAssessmentObservable(portalId, evalueeId, newAssessmentId);
    } catch (error) {
      console.error('Error starting new assessment:', error);
      this.notif.openDismissibleSnackbar('Error starting new assessment. Please try again.');
    }
  }

  async cancelCurrentAssessment(evaluee: Evaluee) {
    const portalId = evaluee.portalId;
    const evalueeId = evaluee.uid;
    const currentAssessmentId = evaluee.currentAssessmentId;
    const assessmentsSnap = await getDocs(collection(this.firestore, `portals/${portalId}/evaluees/${evalueeId}/assessments`));
    const assessments = assessmentsSnap.docs.map(doc => {
      const data = doc.data() as Assessment;
      return { ...data, id: doc.id };
    });
    const currentAssessment = assessments.find(a => a.id === currentAssessmentId);
    const completedAssessments = assessments.filter(a => !!a.completedDate && a.id !== currentAssessmentId);

    if (currentAssessment && !currentAssessment.completedDate) {
      // Delete only the in-progress assessment
      await deleteDoc(doc(this.firestore, `portals/${portalId}/evaluees/${evalueeId}/assessments/${currentAssessmentId}`));
      
      
      
      // Manually trigger portal statistics update
      try {
        // Call the new Firebase Function to force portal statistics update
        const forcePortalStatisticsUpdateCallable = httpsCallable(this.functions, 'forcePortalStatisticsUpdate');
        
        // Set a timeout to avoid hanging
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Client timeout')), 30000); // 30 second timeout
        });
        
        await Promise.race([
          forcePortalStatisticsUpdateCallable({ portalId }),
          timeoutPromise
        ]);
        
        // Force refresh portal data to get updated availableAdmins
        this.ps.loadPortal(portalId, evaluee.counselor?.id || '');
        
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        if (errorMessage === 'Client timeout') {
          console.warn('Portal statistics update timed out during assessment cancellation (this is normal)');
        } else {
          console.warn('Error updating portal statistics during assessment cancellation:', errorMessage);
        }
        // Fallback: refresh portal data
        this.ps.loadPortal(portalId, evaluee.counselor?.id || '');
      }
      
      // Add a small delay to ensure portal counts are updated
      await new Promise(resolve => setTimeout(resolve, 1000));
      const portalDocRef = doc(this.firestore, `portals/${portalId}`);
      if (completedAssessments.length > 0) {
        // Set currentAssessmentId to the latest completed one
        await updateDoc(portalDocRef, {
           availableAdmins: increment(1) as unknown as number,
           lastModifiedDate: new Date(Date.now())
        });
        const latest = completedAssessments.sort((a, b) => {
          const aTime = a.completedDate ? new Date(a.completedDate).getTime() : 0;
          const bTime = b.completedDate ? new Date(b.completedDate).getTime() : 0;
          return bTime - aTime;
        })[0];
       
        await updateDoc(doc(this.firestore, `portals/${portalId}/evaluees/${evalueeId}`), {
          currentAssessmentId: latest.id,
          status: 'completed',
          completedDate: latest.completedDate,
          lastActivity: 'Assessment completed',
          lastActivityDate: latest.completedDate,
          lastModifiedDate: new Date(Date.now()),
        });
      } else {
        // No completed assessments left, delete the evaluee entirely
        // This prevents cancelled assessments from appearing in the Assess tab
        this.deleteEvalueeFromFirestore(evalueeId, evaluee.userId || '', evaluee.registrationId || null, portalId, 'in progress');
        return;
      }
      
      this.notif.openDismissibleSnackbar('Assessment Cancelled!');
    } else {
      // If only one assessment and it's in progress, delete the whole evaluee
      this.deleteEvaluee(evaluee);
    }
  }

  batchAssignCounselorByEvaluees(evaluees: Evaluee[], counselor: Counselor): Observable<void> {
    if (evaluees?.length >= 100) {
      return throwError(() => new Error('Batch size cannot exceed 100 for this action.'));
    }

    const portalId = evaluees[0].portalId;
    const batch = writeBatch(this.firestore);

    for (const evaluee of evaluees) {
      const currentCounselor: Counselor = evaluee.counselor;
      const counselorHistory: CounselorHistory[] = evaluee.counselorHistory || [];
      
      const evalueeDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${evaluee.uid}`);
      batch.update(evalueeDocRef, {
        counselor,
        counselorHistory: [{ counselor: currentCounselor, endDate: new Date(Date.now()) }, ...counselorHistory],
        lastModifiedDate: new Date(Date.now())
      });
    }

    return from(batch.commit());
  }

  // Method to get basic metadata for multiple assessments
  getAssessmentsMetadata(portalId: string, evalueeId: string, assessmentIds: string[]): Observable<Partial<Assessment>[]> {
    if (!assessmentIds || assessmentIds.length === 0) {
      return of([]);
    }

    const assessmentReads = assessmentIds.map(id => {
      const assessmentDocRef = doc(this.firestore, `portals/${portalId}/evaluees/${evalueeId}/assessments/${id}`) as DocumentReference<Assessment>;
      return from(getDoc(assessmentDocRef)).pipe(
        map(docSnap => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              completedDate: data.completedDate ? (data.completedDate as any).toDate() : null,
              lastModifiedDate: data.lastModifiedDate ? (data.lastModifiedDate as any).toDate() : null 
            };
          } else {
            console.warn(`Assessment metadata not found for ID: ${id}`);
            return null; 
          }
        })
      );
    });

    return forkJoin(assessmentReads).pipe(
      map(results => results.filter(res => res !== null) as Partial<Assessment>[]) // Filter out null results
    );
  }
}
